import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "urql";

import type {
  CancelTrainingRunInput,
  CreateStyleInput,
  CreateTrainingRunInput,
  DeleteStyleFinetuneInput,
  DeleteStyleInput,
  SessionInferenceMode,
  UpdateStyleInput,
  UpdateTrainingRunInput,
} from "@/components/graphql/schema.graphql";
import { InferenceFormNavigationState } from "@/components/inference-forms/constants";
import { STYLE_FIELD_NAME } from "@/components/inference-forms/fields/Style/constants";
import { isSessionCanvasMode } from "@/components/inference-forms/utils/utils";
import { FIELD_NAMES } from "@/components/InferenceFormComposer/FieldDefs/constants";

import { useUrqlResponseHandler } from "../useUrqlResponseHandler";
import {
  CancelTrainingRun,
  CreateStyle2,
  CreateTrainingRun,
  DeleteStyle2,
  DeleteStyleFinetune,
  UpdateStyle2,
  UpdateTrainingRun,
} from "./queries.graphql";

export const useStyleActions = () => {
  const navigate = useNavigate();
  const [createMutation, executeCreation] = useMutation(CreateStyle2);
  const [updateMutation, executeUpdate] = useMutation(UpdateStyle2);
  const [deleteMutation, executeDelete] = useMutation(DeleteStyle2);
  const [createTrainingRunMutation, executeCreateTrainingRun] =
    useMutation(CreateTrainingRun);
  const [updateTrainingRunMutation, executeUpdateTrainingRun] =
    useMutation(UpdateTrainingRun);
  const [cancelTrainingRunMutation, executeCancelTrainingRun] =
    useMutation(CancelTrainingRun);
  const [deleteStyleFinetuneMutation, executeDeleteStyleFinetune] =
    useMutation(DeleteStyleFinetune);

  const { getResponseFromState } = useUrqlResponseHandler();

  const createStyle = useCallback(
    async (input: CreateStyleInput, successMessage?: string) => {
      const state = await executeCreation({
        createStyleInput: input,
      });
      const { data, error } = getResponseFromState({
        state,
        successMessage: successMessage || "Model created",
        errorMessage: "Unfortunately we could not create your model",
      });
      if (error) {
        throw new Error(error.message);
      }
      return data;
    },
    [executeCreation, getResponseFromState],
  );

  const updateStyle = useCallback(
    async (
      input: UpdateStyleInput,
      options: {
        workspaceName?: string; // will fetch `isLinkedToWorkspace` if defined
        successMessage?: string;
      } = {},
    ) => {
      const { workspaceName = null, successMessage } = options;
      const state = await executeUpdate({
        updateStyleInput: input,
        workspaceName,
        includeWorkspaceLink: Boolean(workspaceName),
      });
      const { data, error } = getResponseFromState({
        state,
        successMessage: successMessage ?? "Model updated",
        errorMessage: "Unfortunately we could not update your model",
      });
      if (error) {
        throw new Error(error.message);
      }
      return data;
    },
    [executeUpdate, getResponseFromState],
  );

  const deleteStyle = useCallback(
    async (input: DeleteStyleInput, successMessage?: string) => {
      const state = await executeDelete({
        deleteStyleInput: input,
      });
      const { data, error } = getResponseFromState({
        state,
        successMessage: successMessage ?? "Model deleted",
        errorMessage: "Unfortunately we could not delete your model",
      });
      if (error) {
        throw new Error(error.message);
      }
      return data;
    },
    [executeDelete, getResponseFromState],
  );

  const createTrainingRun = useCallback(
    async (input: CreateTrainingRunInput, successMessage?: string) => {
      const state = await executeCreateTrainingRun({
        createTrainingRunInput: input,
      });
      const { data, error } = getResponseFromState({
        state,
        successMessage: successMessage ?? "Training started",
        errorMessage: "Unfortunately we could not start the training",
      });
      if (error) {
        throw new Error(error.message);
      }
      return data;
    },
    [executeCreateTrainingRun, getResponseFromState],
  );

  const updateTrainingRun = useCallback(
    async (input: UpdateTrainingRunInput, successMessage?: string) => {
      const state = await executeUpdateTrainingRun({
        updateTrainingRunInput: input,
      });
      const { data, error } = getResponseFromState({
        state,
        successMessage: successMessage ?? "Training updated",
        errorMessage: "Unfortunately we could not update the training",
      });
      if (error) {
        throw new Error(error.message);
      }
      return data;
    },
    [executeUpdateTrainingRun, getResponseFromState],
  );

  const cancelTrainingRun = useCallback(
    async (input: CancelTrainingRunInput, successMessage?: string) => {
      const state = await executeCancelTrainingRun({
        cancelTrainingRunInput: input,
      });
      const { data, error } = getResponseFromState({
        state,
        successMessage: successMessage ?? "Training canceled",
        errorMessage: "Unfortunately we could not cancel the training",
      });
      if (error) {
        throw new Error(error.message);
      }
      return data;
    },
    [executeCancelTrainingRun, getResponseFromState],
  );

  const deleteStyleFinetune = useCallback(
    async (input: DeleteStyleFinetuneInput, successMessage?: string) => {
      const state = await executeDeleteStyleFinetune({
        deleteStyleFinetuneInput: input,
      });
      const { data, error } = getResponseFromState({
        state,
        successMessage: successMessage ?? "Model deleted successfully.",
        errorMessage: "Unfortunately we could not delete the model",
      });
      if (error) {
        throw new Error(error.message);
      }
      return data;
    },
    [executeDeleteStyleFinetune, getResponseFromState],
  );

  const navigateToForgeWithStyle = useCallback(
    (
      workspaceName: string,
      styleId: string,
      primaryInferenceMode: SessionInferenceMode,
    ) => {
      const styleFieldName = isSessionCanvasMode(primaryInferenceMode)
        ? STYLE_FIELD_NAME
        : FIELD_NAMES.model;
      const navigationState: InferenceFormNavigationState = {
        form: {
          [styleFieldName]: { id: styleId, weight: 100 },
          ...(styleFieldName === FIELD_NAMES.model && {
            [FIELD_NAMES.modelBlends]: [{ id: styleId, weight: 100 }],
          }),
        },
        mode: primaryInferenceMode,
      };
      navigate(`/${workspaceName}/session/new`, {
        state: navigationState,
      });
    },
    [navigate],
  );

  return {
    createStyle: {
      execute: createStyle,
      state: createMutation,
    },
    updateStyle: {
      execute: updateStyle,
      state: updateMutation,
    },
    deleteStyle: {
      execute: deleteStyle,
      state: deleteMutation,
    },
    createTrainingRun: {
      execute: createTrainingRun,
      state: createTrainingRunMutation,
    },
    updateTrainingRun: {
      execute: updateTrainingRun,
      state: updateTrainingRunMutation,
    },
    cancelTrainingRun: {
      execute: cancelTrainingRun,
      state: cancelTrainingRunMutation,
    },
    deleteStyleFinetune: {
      execute: deleteStyleFinetune,
      state: deleteStyleFinetuneMutation,
    },
    navigateToForgeWithStyle,
  };
};
